import { invalidateLoadingOrders } from '@/services/formService';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { HubConnectionState, type HubConnection } from "@microsoft/signalr";
import { useLocation } from "react-router-dom";
import type { HubEvent } from "@/models/api";
import { useAuth } from "@/context/AuthContext";
import { adminRoles } from "@/helpers/roles";
import {
  createCrmConnection,
  crmHubEvents,
  type CrmHubEventName,
} from "@/services/crmHub";
import { updatePresence, type ActionPresence } from "@/services/actionPresence";
import { createLiveRefreshQueue, viewTopics } from "@/services/liveRefreshQueue";
export type HubStatus =
  "idle" | "connecting" | "connected" | "reconnecting" | "disconnected";
export interface LiveEvent {
  name: CrmHubEventName;
  payload: HubEvent;
  at: string;
}
interface SignalRContextValue {
  status: HubStatus;
  lastEvent: LiveEvent | null;
  actionPresence: ActionPresence;
  openOrderCount: number | null;
  ordersAttention: boolean;
  replaceOpenOrderCount: (count: number) => void;
  viewAction: (id: string) => () => void;
  notify: (method: string, payload: Record<string, unknown>) => Promise<void>;
  subscribe: (listener: (event: LiveEvent) => void) => () => void;
  reconnect: () => void;
  watch: (topic: string) => () => void;
}
const SignalRContext = createContext<SignalRContextValue>({
  status: "idle",
  lastEvent: null,
  actionPresence: {},
  openOrderCount: null,
  ordersAttention: false,
  replaceOpenOrderCount: () => {},
  viewAction: () => () => {},
  notify: async () => {},
  subscribe: () => () => {},
  reconnect: () => {},
  watch: () => () => {},
});
export function SignalRProvider({ children }: { children: ReactNode }) {
  const { session, isDemo } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<HubStatus>("idle");
  const [lastEvent, setLastEvent] = useState<LiveEvent | null>(null);
  const [actionPresence, setPresence] = useState<ActionPresence>({});
  const [openOrderCount, setOpenOrderCount] = useState<number | null>(null);
  const [ordersAttention, setOrdersAttention] = useState(false);
  const [, setWatchRevision] = useState(0);
  const watches = useRef(new Map<string, number>());
  const watch = useCallback((topic: string) => {
    watches.current.set(topic, (watches.current.get(topic) ?? 0) + 1);
    setWatchRevision(value => value + 1);
    return () => {
      const count = (watches.current.get(topic) ?? 1) - 1;
      if (count) watches.current.set(topic, count); else watches.current.delete(topic);
      setWatchRevision(value => value + 1);
    };
  }, []);
  const [visible, setVisible] = useState(!document.hidden);
  const topics = [...new Set([...viewTopics(location.pathname), ...watches.current.keys()])].sort();
  const topicKey = visible ? topics.join('|') : '';
  const desiredTopics = useRef<string[]>([]);
  desiredTopics.current = visible ? topics : [];
  const subscriptionQueue = useRef<Promise<unknown>>(Promise.resolve());
  const queueCatchUp = useRef<(() => void) | null>(null);
  const updateSubscriptions = useCallback((connection: HubConnection) => {
    const next = [...desiredTopics.current];
    const task = subscriptionQueue.current.catch(() => {}).then(async () => {
      if (connectionRef.current === connection && connection.state === HubConnectionState.Connected)
        await connection.invoke('SetSubscriptions', next);
    });
    subscriptionQueue.current = task.catch(() => {});
    return task;
  }, []);
  const [viewedActionId, setViewedActionId] = useState<string | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const connectionRef = useRef<HubConnection | null>(null);
  const sends = useRef<Promise<unknown>>(Promise.resolve());
  const announced = useRef<{ actionId: string; userId: string; connection: HubConnection } | null>(null);
  const countOrders = useRef(false);
  const countVersion = useRef(0);
  countOrders.current = Boolean(session?.role && adminRoles.includes(session.role));
  const reviewingOrders = useRef(false);
  reviewingOrders.current = location.pathname === "/orders";
  const listeners = useRef(new Set<(event: LiveEvent) => void>());
  const subscribe = useCallback((listener: (event: LiveEvent) => void) => {
    listeners.current.add(listener);
    return () => { listeners.current.delete(listener); };
  }, []);
  const send = useCallback(
    (connection: HubConnection, method: string, payload: unknown) => {
      const task = sends.current
        .catch(() => {})
        .then(async () => {
          if (
            connectionRef.current !== connection ||
            connection.state !== HubConnectionState.Connected
          )
            return;
          await connection.invoke(method, payload);
          const message = payload as { actionId: string; userId: string };
          if (method === "SendActionLocked") announced.current = {...message, connection};
          else if (announced.current?.connection === connection && announced.current?.actionId === message.actionId)
            announced.current = null;
        });
      sends.current = task.catch(() => {});
      return task;
    },
    [],
  );
  const accessToken =
    session?.purpose === "access" ? session.accessToken : null;
  const notify = useCallback((method: string, payload: Record<string, unknown>) => {
    if (method !== "SendActionLocked" && method !== "SendActionUnlocked") return Promise.resolve();
    const connection = connectionRef.current;
    if (!connection) return Promise.resolve();
    return send(connection, method, payload).then(() => undefined);
  }, [send]);
  const reconnect = useCallback(() => setConnectionAttempt((attempt) => attempt + 1), []);
  const viewAction = useCallback((id: string) => {
    setViewedActionId(id);
    return () =>
      setViewedActionId((current) => (current === id ? null : current));
  }, []);
  useEffect(() => {
    setPresence({});
    setLastEvent(null);
    if (!accessToken || isDemo) {
      setStatus("idle");
      return;
    }
    let stopped = false;
    const refreshes = createLiveRefreshQueue(changes => {
      if (stopped) return;
      // A request begun before an invalidation must not supply a stale shared response.
      invalidateLoadingOrders(changes.some(change => !change.operation) ? undefined : changes.flatMap(change => change.orderIds ?? []));
      const event: LiveEvent = { name: 'WorkspaceChanged', payload: { businessId: session?.businessId, changes } as HubEvent, at: new Date().toISOString() };
      setLastEvent(event);
      listeners.current.forEach(listener => listener(event));
    });
    queueCatchUp.current = () => refreshes.enqueue({});
    refreshes.setActive(!document.hidden);
    const visibility = () => refreshes.setActive(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    const connection = createCrmConnection();
    connectionRef.current = connection;
    setStatus("connecting");
    for (const name of crmHubEvents)
      connection.on(name, (payload: HubEvent) => {
        if (stopped) return;
        // The new API should scope messages server-side; also ignore explicitly foreign businesses.
        if (!payload || typeof payload !== "object") return;
        const record = payload as unknown as Record<string, unknown>;
        const businessId = payload.businessId ?? record.BusinessId;
        if (typeof businessId === "string" && session?.businessId && businessId.toLowerCase() !== session.businessId.toLowerCase()) return;
        if (name === 'PresenceSnapshot' && Array.isArray(record.viewers)) {
          setPresence(record.viewers.reduce<ActionPresence>((all, viewer) => updatePresence(all, 'ActionLocked', viewer), {}));
          return;
        }
        if (name === 'OrderCountChanged') {
          countVersion.current++;
          if (typeof record.count === 'number' && countOrders.current) setOpenOrderCount(record.count);
          if (record.newOrder && !reviewingOrders.current) setOrdersAttention(true);
          return;
        }
        if (name === 'WorkspaceChanged') {
          refreshes.enqueue(payload);
          return;
        }
        const event = { name, payload, at: new Date().toISOString() };
        setLastEvent(event);
        listeners.current.forEach(listener => listener(event));
        setPresence(current => updatePresence(current, name, payload));
      });
    connection.onreconnecting(() => {
      if (!stopped) {
        setStatus("reconnecting");
        setPresence({});
      }
    });
    connection.onreconnected(() => {
      if (!stopped) { setStatus("connected"); }
    });
    connection.onclose(() => {
      if (!stopped) {
        setStatus("disconnected");
        setPresence({});
      }
    });
    void connection
      .start()
      .then(() => {
        if (!stopped) { setStatus("connected"); }
      })
      .catch(() => {
        if (!stopped) setStatus("disconnected");
      });
    return () => {
      stopped = true;
      queueCatchUp.current = null;
      refreshes.dispose();
      document.removeEventListener('visibilitychange', visibility);
      if (connectionRef.current === connection) connectionRef.current = null;
      void sends.current
        .catch(() => {})
        .then(async () => {
          const open = announced.current;
          if (open?.connection === connection && connection.state === HubConnectionState.Connected) {
            try {
              await connection.invoke("SendActionUnlocked", {
                actionId: open.actionId,
                userId: open.userId,
              });
            } catch {
              /* Server disconnect cleanup must also expire presence. */
            }
          }
          if (announced.current?.connection === connection) announced.current = null;
          await connection.stop();
        });
    };
  }, [accessToken, isDemo, session?.businessId, connectionAttempt, updateSubscriptions]);
  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, []);
  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || status !== 'connected') return;
    let cancelled = false;
    void updateSubscriptions(connection).then(async () => {
      if (cancelled || !visible) return;
      if (desiredTopics.current.includes('presence')) {
        const snapshot = await connection.invoke<Array<{ actionId: string; userId: string; username: string }>>('GetActionPresence');
        if (!cancelled) setPresence(snapshot.reduce<ActionPresence>((all, viewer) => updatePresence(all, 'ActionLocked', viewer), {}));
      }
      // New views load themselves; visible-again views must catch up on missed events.
      if (!cancelled) queueCatchUp.current?.();
    }).catch(() => { if (!cancelled) setStatus('disconnected'); });
    return () => { cancelled = true };
  }, [topicKey, visible, status, updateSubscriptions, session?.businessId]);
  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || status !== 'connected' || !countOrders.current) return;
    let cancelled = false;
    const version = countVersion.current;
    void connection.invoke<number>('GetOpenOrderCount').then(count => {
      if (!cancelled && countVersion.current === version) setOpenOrderCount(count);
    }).catch(() => {});
    return () => { cancelled = true };
  }, [status, session?.businessId]);
  useEffect(() => {
    if (location.pathname === "/orders") setOrdersAttention(false);
  }, [location.pathname]);
  const replaceOpenOrderCount = useCallback((count: number) => { setOpenOrderCount(count) }, []);
  useEffect(() => {
    setOpenOrderCount(null);
    setOrdersAttention(false);
  }, [session?.businessId, isDemo]);
  useEffect(() => {
    const connection = connectionRef.current;
    if (
      !viewedActionId ||
      !visible ||
      !session?.userId ||
      isDemo ||
      status !== "connected" ||
      !connection
    )
      return;
    const payload = {
      actionId: viewedActionId,
      userId: session.userId,
      username: session.fullName || session.emailAddress,
    };
    void send(connection, "SendActionLocked", payload).catch(() => {});
    return () => {
      void send(connection, "SendActionUnlocked", {
        actionId: payload.actionId,
        userId: payload.userId,
      }).catch(() => {});
    };
  }, [
    viewedActionId,
    visible,
    status,
    isDemo,
    session?.userId,
    session?.fullName,
    session?.emailAddress,
    send,
  ]);
  // Explicit sample presence only in the demo workspace; never connect demo data to the hub.
  const presence = isDemo
    ? {
        "2": {
          actionId: "2",
          userId: "demo-jamie",
          username: "Jamie Taylor (demo)",
        },
      }
    : actionPresence;
  const value = useMemo(
    () => ({ status, lastEvent, actionPresence: presence, openOrderCount, ordersAttention, replaceOpenOrderCount, viewAction, notify, subscribe, reconnect, watch }),
    [status, lastEvent, presence, openOrderCount, ordersAttention, replaceOpenOrderCount, viewAction, notify, subscribe, reconnect, watch],
  );
  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
}
export function useCrmHub() {
  return useContext(SignalRContext);
}

export function useCrmSubscription(topic: string | null) {
  const { watch } = useCrmHub();
  useEffect(() => topic ? watch(topic) : undefined, [topic, watch]);
}
